import Viewer from 'viewerjs';
import 'viewerjs/dist/viewer.css';
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

export class ImageViewer {
  constructor(container, { protect = false } = {}) {
    if (!container) return;

    this.protect = protect;
    this.$container = $(container);
    this.downloadIconPromise = protect ? Promise.resolve(null) : window.fetchIcon('download');

    const toolbar = {
      zoomIn: true,
      zoomOut: true,
      oneToOne: true,
      reset: true,
      prev: true,
      play: true,
      next: true,
      rotateLeft: true,
      rotateRight: true,
      flipHorizontal: true,
      flipVertical: true,
      fullscreen: {
        show: true,
        size: 'medium',
        click: () => this.viewer.viewer.requestFullscreen(),
        text: '',
      },
      ...(!protect && {
        download: {
          show: true,
          size: 'medium',
          click: () => this.downloadFile(this.viewer.images[this.viewer.index]),
          text: '',
        },
      }),
    };

    this.viewer = new Viewer(container, {
      loop: false,
      transition: false,
      url: (img) => img.dataset.full || img.src,
      toolbar,
      view: () => this.handleView(),
      ready: () => {
        if (protect) {
          this.viewer.viewer.addEventListener('contextmenu', (e) => e.preventDefault());
        }
        this.loadIcons();
      },
    });
  }

  async downloadFile(original) {
    if (this.protect || !original) return;

    const fileUrl = original.dataset.full || original.src;
    const downloadUrl = original.dataset.download || fileUrl;
    const fileName = original.alt?.trim() || fileUrl.split('/').pop() || 'download';

    if (downloadUrl !== fileUrl) {
      window.location.href = downloadUrl;
      return;
    }

    try {
      const buffer = await fetch(fileUrl).then((r) => r.arrayBuffer());
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.location.href = downloadUrl;
    }
  }

  loadIcons() {
    const $viewer = $(this.viewer.viewer);

    if (!this.protect) {
      const $download = $viewer.find('.viewer-download');
      if (!$download.children().length) {
        window.fetchIcon('file-down').then((svg) => $download.empty().append(svg));
      }
    }

    const $fullscreen = $viewer.find('.viewer-fullscreen');
    if (!$fullscreen.children().length) {
      window.fetchIcon('maximize-2').then((svg) => $fullscreen.empty().append(svg));
    }
  }

  async renderPdf(url, $canvas) {
    const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
    GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

    const $wrap = $('<div>', { class: 'viewer__pdf-pages' }).appendTo($canvas);
    $wrap[0].addEventListener('contextmenu', (e) => e.preventDefault());

    let pdf;
    try {
      pdf = await getDocument(url).promise;
    } catch {
      $wrap.text('Failed to load PDF.');
      return;
    }

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = ($wrap[0].clientWidth || 800) / baseViewport.width;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      $wrap.append(canvas);

      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    }
  }

  async handleView() {
    requestAnimationFrame(async () => {
      const $canvas = $(this.viewer?.viewer).find('.viewer-canvas');
      if (!$canvas.length) return;

      const original = this.viewer?.images[this.viewer.index];
      if (!original) return;

      const url = original.dataset.full || original.src;
      const type = original.dataset.type;

      $canvas.find('embed, .viewer__download-wrapper, .viewer__pdf-pages, .button.button--primary').remove();
      const $img = $canvas.find('img').show();
      const $toolbar = $(this.viewer?.viewer).find('.viewer-toolbar');

      if (type === 'application/pdf') {
        $img.hide();
        $toolbar.show().find('li').hide();
        $toolbar.find('.viewer-fullscreen').show();

        if (this.protect) {
          await this.renderPdf(url, $canvas);
        } else {
          $('<embed>', {
            src: `${url}#page=1&navpanes=0&pagemode=none&view=FitH&zoom=page-width`,
            type: 'application/pdf',
          }).appendTo($canvas);
          $toolbar.find('.viewer-download').show();
        }
      } else if (type?.startsWith('image/')) {
        $toolbar.show().find('li').show();
      } else {
        $img.hide();
        $toolbar.hide();

        if (!this.protect) {
          const $btn = $('<a>', {
            href: url,
            download: original.alt || 'download',
            class: 'button button--primary viewer__download-wrapper',
          }).append(`<span>Download ${original.alt || url.split('/').pop()}</span>`);

          const svg = await this.downloadIconPromise;
          if (svg) $btn.prepend(svg);

          $btn.off('click').on('click', (e) => {
            e.preventDefault();
            this.downloadFile(original);
          });

          $canvas.append($btn);
        }
      }

      $(this.viewer.viewer).find('.viewer-list li').each((_, li) => {
        const $li = $(li);
        const index = $li.data('index');
        const originalEl = this.viewer.images[index];
        if (!originalEl) return;

        const navType = originalEl.dataset.type;
        if (!navType?.startsWith('image/')) {
          $li.find('img').hide();
          const wrapperSVG = $(originalEl).closest('figure').find('svg').first();
          if (wrapperSVG.length) {
            $li.find('svg').remove();
            $li.prepend(wrapperSVG.clone());
          }
        }

        $li.find('svg').off('click.nav-svg').on('click.nav-svg', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.viewer.view(index);
        });
      });
    });
  }

  view(index) {
    if (!this.viewer) return;

    this.viewer.view(index);
    $(this.viewer.viewer).one('hidden', () => this.cleanup());
  }

  update() {
    if (!this.viewer) return;

    this.viewer.destroy();
    this.viewer = new Viewer(this.$container[0], {
      loop: false,
      movable: false,
      transition: false,
      url: (img) => img.dataset.full || img.src,
      toolbar: this.viewer.options.toolbar,
      view: () => this.handleView(),
      ready: () => this.loadIcons(),
    });
  }

  cleanup() {
    const $canvas = $(this.viewer?.viewer).find('.viewer-canvas');
    if (!$canvas.length) return;

    $canvas.find('embed, .viewer__download-wrapper, .viewer__pdf-pages').remove();
    $canvas.find('img, svg').show();
    const $toolbar = $(this.viewer?.viewer).find('.viewer-toolbar');
    $toolbar.show().find('li').show();
  }
}
